<template>
  <v-dialog
    :model-value="show"
    @update:model-value="
      {
        if (!state.loading) $emit('update:show', $event);
      }
    "
    :max-width="selected && fileText ? '1400px' : '1000px'"
    :fullscreen="$vuetify.display.xs"
  >
    <v-card>
      <v-card-title
        class="pb-0"
        :style="$vuetify.display.smAndDown ? {} : { backgroundColor: 'rgb(242, 242, 242)' }"
      >
        <span class="d-flex align-center justify-space-between">
          <typography v-if="$vuetify.display.smAndDown">Upload Csv Integration</typography>
          <div class="tabs-container" v-else>
            <template v-for="(item, i) in options" :key="item.value">
              <div
                :class="['tab-item', { selected: item.value === state.selectedName }]"
                @click="state.selectedName = item.value"
              >
                {{ item.title }}
                <div></div>
              </div>
              <div
                class="tab-divider"
                v-if="
                  item.value !== state.selectedName && options[i + 1]?.value !== state.selectedName
                "
              >
                <div></div>
              </div>
            </template>
          </div>
          <v-btn
            icon
            color="normal"
            variant="text"
            @click="
              {
                if (!state.loading) $emit('update:show', false);
              }
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </span>
        <v-divider class="my-0" style="width: calc(100% + 40px); margin-left: -20px"></v-divider>
      </v-card-title>
      <v-card-item>
        <div class="my-3">
          <div class="d-flex align-center justify-space-between flex-wrap gap-3">
            <v-select
              color="primary"
              :items="options"
              item-title="title"
              item-value="value"
              label="Upload Type"
              v-model:model-value="state.selectedName"
              style="max-width: 350px; min-width: 200px"
              density="comfortable"
              class="custom-input"
              v-if="$vuetify.display.smAndDown"
            ></v-select>
            <div v-else></div>
            <v-btn v-if="state.selectedName" @click="downloadPreview">Download Csv Preview</v-btn>
          </div>
          <div
            :class="['file-select-con', { 'drag-over': state.dragOver }]"
            @drop="(event) => onDrop(event)"
            @dragover="(event) => onDragOver(event)"
            @dragleave="state.dragOver = false"
            tabindex="1"
          >
            <typography color="primary700" weight="600" size="1.15rem">
              Drag and Drop Files Here
            </typography>

            <typography>or</typography>
            <v-btn
              color="primary"
              @click="(event) => event.currentTarget.querySelector('input').click()"
              :disabled="state.loading"
              >Browse Files
              <input
                ref="fileRef"
                accept="text/csv"
                type="file"
                style="display: none"
                v-on:change="setFile(($event.currentTarget as any).files[0])"
              />
            </v-btn>
            <div class="file-validation">
              <div v-if="file">{{ file.name }}</div>
              <div
                :class="['validation-status', { invalid: !isValid.valid }]"
                v-if="file && state.selectedName"
              >
                <v-icon :color="isValid.valid ? 'rgb(4, 231, 4)' : 'rgb(255, 0, 0)'">{{
                  isValid.valid ? "mdi-check-decagram" : "mdi-close-circle"
                }}</v-icon>
                <span>
                  {{ isValid.valid ? "File is Valid" : "File is Invalid" }}
                </span>
              </div>
              <div class="error-list" v-if="file && state.selectedName">
                <template v-for="(message, i) in isValid.validMessages" :key="i">
                  <div :class="`${message.type}-item`">
                    {{ message.message }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </v-card-item>
      <v-card-actions v-if="isValid.valid">
        <div class="d-flex w-100 justify-content-end px-3 py-1">
          <v-btn :loading="state.loading" @click="uploadCsv" variant="flat">Upload</v-btn>
        </div>
      </v-card-actions>
      <v-divider v-if="selected && fileText"></v-divider>
      <v-card-item v-if="selected && fileText">
        <!-- preview table -->
        <PreviewTable :csv-type="selected" :file-str="fileText"></PreviewTable>
      </v-card-item>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import Typography from "@/components/typography.vue";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import downloadFile from "@/utils";
import { asyncComputed } from "@/utils/asyncComputed";
import { ref } from "vue";
import { reactive } from "vue";
import { computed } from "vue";
import { validateCsv } from "../integration/utils";
import PreviewTable from "../integration/previewTable.vue";
import { useSchoolStore } from "@/stores/school";
import { isStudentLimitError } from "@/utils/studentLimitError";
const props = defineProps<{
  show: boolean;
  tab: "teacher" | "parent" | "student" | "team";
}>();

defineEmits<{
  (e: "update:show", ...args: any[]): void;
}>();

const file = ref<File | null>(null);
const fileRef = ref<HTMLInputElement | null>(null);
const alertStore = useAlertsStore();
const schoolStore = useSchoolStore();

const parenChildRelationShip = ["parent_localid", "student_localid"];
const teacherChildRelationShip = ["student_localid", "teacher_localid"];
const teacherTeamRelationShip = ["team_name", "teacher_localid", "room", "virtual_room"];
const TeamStudentRelationShip = ["team_name", "student_localid"];

type TCsvType = { name: string; fields: string[]; title: string; fileName: string };

const csvTypes: Record<typeof props.tab, TCsvType[]> = {
  teacher: [
    {
      name: "teachers",
      fields: [
        "teacher_localid",
        "firstname",
        "lastname",
        "email",
        "phonenumber",
        "titledescription",
        "virtual_room",
        "room",
        "username",
        "password"
      ],
      title: "Teachers",
      fileName: "teacher"
    },
    {
      name: "teacher_student",
      fields: teacherChildRelationShip,
      title: "Enrollment",
      fileName: "enrollment"
    }
  ],
  parent: [
    {
      name: "parents",
      fields: [
        "parent_localid",
        "firstname",
        "lastname",
        "email",
        "phonenumber",
        "username",
        "password"
      ],
      title: "Parents",
      fileName: "parent"
    },
    {
      name: "parent_student",
      fields: parenChildRelationShip,
      title: "Relationships",
      fileName: "relationship"
    }
  ],
  student: [
    {
      name: "students",
      fields: [
        "student_localid",
        "firstname",
        "lastname",
        "description",
        "email",
        "phonenumber",
        "username",
        "password"
      ],
      title: "Students",
      fileName: "student"
    },
    {
      name: "parent_student",
      fields: parenChildRelationShip,
      title: "Relationships",
      fileName: "relationship"
    },
    {
      name: "teacher_student",
      fields: teacherChildRelationShip,
      title: "Enrollment",
      fileName: "enrollment"
    },
    {
      name: "team_student",
      fields: TeamStudentRelationShip,
      title: "Team Enrollment",
      fileName: "team_enrollment"
    }
  ],
  team: [
    {
      name: "teacher_team",
      fields: teacherTeamRelationShip,
      title: "Teams",
      fileName: "team"
    },
    {
      name: "team_student",
      fields: TeamStudentRelationShip,
      title: "Team Enrollment",
      fileName: "team_enrollment"
    }
  ]
};

const state = reactive({
  selectedName: csvTypes[props.tab][0]?.name || "",
  dragOver: false,
  loading: false
});

const options = computed(() => csvTypes[props.tab].map((i) => ({ value: i.name, title: i.title })));
const setFile = (val) => {
  if (state.loading) return;
  state.dragOver = false;
  if (val?.type == "text/csv") {
    file.value = val;
  } else {
    if (val) useAlertsStore().addAlert("error", "File upload only supports csv files", 7);
  }
};

const onDrop = (event) => {
  if (state.loading) return;
  event.preventDefault();
  setFile(event.dataTransfer?.files[0]);
};

const onDragOver = (event) => {
  if (state.loading) return;
  if (!state.dragOver) {
    state.dragOver = true;
  }
  event.preventDefault();
};

const downloadPreview = () => {
  if (state.loading) return;
  const selected = csvTypes[props.tab].find((i) => i.name === state.selectedName);
  if (selected) {
    downloadFile(selected.fields.join(",") + "\n", `${selected.title}.csv`, "text/csv");
  }
};

const { data: fileText } = asyncComputed(
  async () => {
    if (file.value) {
      return await file.value.text();
    }
    return "";
  },
  [() => file.value?.name, file, () => file.value?.webkitRelativePath],
  undefined,
  undefined,
  ""
);

const selected = computed(() => csvTypes[props.tab].find((i) => i.name === state.selectedName));

const isValid = computed(() => {
  if (selected.value && fileText.value) {
    return validateCsv(fileText.value, selected.value, true);
  }
  return { valid: false, validMessages: [] };
});

const uploadCsv = async () => {
  state.loading = true;
  const selected = csvTypes[props.tab].find((i) => i.name === state.selectedName)!;
  if (selected && isValid.value.valid) {
    const name = `${selected.fileName}-${schoolStore.school_id}.csv`;
    const data = new FormData();
    data.append("file", file.value!, name);

    const res = await FetchService.fetchData("/csv-upload", "post", data);
    if (!isStudentLimitError(res)) {
      if (typeof res?.message === "string" && res.message) {
        res.message = [res?.message];
      }
      res?.message?.forEach?.((i) => alertStore.addAlert(res.status ? "success" : "error", i));
    }
    if (res?.status) {
      if (fileRef.value) {
        fileRef.value.type = "";
        fileRef.value.type = "file";
      }
      file.value = null;
    }
  }
  state.loading = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.file-select-con {
  padding: 20px;
  border: 3px dashed $gray-400;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 20px auto;
  max-width: 500px;
  border-radius: 8px;
  background-color: $primary-50;

  &.drag-over {
    background-color: $primary-75;
  }
}

.file-validation {
  width: 100%;
  & > div:first-child {
    text-align: center;
    width: 100%;
  }

  .validation-status {
    user-select: none;
    background-color: $gray-50;
    padding: 10px;
    margin-top: 10px;
    margin-bottom: 10px;
    border-radius: 4px;
    border: 1px solid $gray-200;

    color: rgb(4, 231, 4);
    display: flex;
    align-items: center;
    gap: 10px;

    &.invalid {
      color: rgb(255, 0, 0);
    }
  }
}

.tabs-container {
  display: flex;
  align-items: center;
  justify-content: left;
  background-color: rgb(242, 242, 242);
  align-self: end;
  user-select: none;
  .tab-item {
    z-index: 999;
    padding: 2px 10px;
    cursor: pointer;
    // border-right: 2px solid gray;
    &:hover {
      background-color: rgb(248, 248, 248);
    }

    &.selected {
      background-color: white;
      border-top-right-radius: 8px;
      border-top-left-radius: 8px;
      position: relative;
      div {
        content: " ";
        position: absolute;
        top: 92%;
        left: 0;
        height: 20px;
        width: 100%;
        background-color: white;
        z-index: 999;
      }
    }

    &:first-child {
      margin-left: 2px;
    }
    &:last-child {
      margin-right: 2px;
    }
  }

  .tab-divider:not(:last-child) {
    display: flex;
    align-self: stretch;
    align-items: center;
    div {
      height: 60%;
      width: 1px;
      background: gray;
    }
  }
}

.error-item {
  color: rgb(255, 0, 0);
}

.warning-item {
  color: #ff9200;
}
</style>
